import { LineChart, TrendingUp, Briefcase, Database } from 'lucide-react';

export const jobOpenings = [
  {
    id: 'jr-business-analyst',
    title: 'Jr. Business Analyst',
    department: 'Business Analyst',
    icon: LineChart,
    location: 'Canada, USA, UAE',
    workplaceType: 'Hybrid',
    type: 'Full Time',
    experience: 'Fresher / Entry Level',
    postedDate: 'Active',
    summary:
      'Motivated and detail-oriented Business Analyst (Fresher) who is eager to learn and build a career in business analysis, working closely with senior BAs, PMs, designers, and developers.',
    overview:
      'We are looking for a motivated and detail-oriented Business Analyst (Fresher) who is eager to learn and build a career in business analysis. The candidate will work closely with senior BAs, project managers, designers, and developers to understand business requirements, document them clearly, and support the delivery of quality solutions.',
    sectionHeaders: {
      responsibilities: 'Key Responsibilities',
      requirements: 'Core Skills',
      niceToHave: 'Preferred Qualities',
      benefits: 'What You Will Learn',
    },
    responsibilities: [
      'Gather and understand business requirements from internal teams or clients.',
      'Prepare clear and structured documents: Business Requirement Document (BRD), Functional Requirement Document (FRD), User Stories / Use Cases, and Process Flows / Workflows.',
      'Maintain requirement traceability and version control throughout project development.',
      'Analyze business problems and suggest practical, scalable solutions.',
      'Identify gaps, dependencies, and edge cases in functional requirements.',
      'Support senior BAs in preparing project scope, effort estimations, and project understanding documents.',
      'Act as a coordination point between business teams, UI/UX design, and development teams.',
      'Participate actively in requirement discussions, internal team sprint meetings, and client calls.',
      'Take structured meeting notes and distribute MOM (Minutes of Meeting) with clear action items.',
    ],
    requirements: [
      'Good understanding of SDLC and Agile concepts (basic knowledge).',
      'Strong analytical and logical thinking capabilities.',
      'Good written and verbal communication skills.',
      'Ability to create clear and structured documentation.',
      'Attention to detail and a proactive problem-solving mindset.',
      'Exposure to common modern AI tools (e.g., ChatGPT).',
    ],
    niceToHave: [
      'Strong willingness to learn, adapt, and grow in a fast-paced environment.',
      'Ability to work collaboratively in cross-functional team environments.',
      'Good coordination, organization, and interpersonal skills.',
      'Active interest in Product Development, IT Services, and Modern Digital Platforms.',
    ],
    benefits: [
      'End-to-end requirement lifecycle mastery.',
      'Direct exposure to client communication and discovery processes.',
      'Hands-on experience in product thinking and technical solution design.',
      'Real-world documentation standards used in global enterprise projects.',
      'Mentorship from seasoned Senior Business Analysts and Technical Leads.',
    ],
  },
  {
    id: 'it-business-development-specialist',
    title: 'IT Business Development Specialist',
    department: 'Business Development',
    icon: TrendingUp,
    location: 'Canada',
    workplaceType: 'Hybrid',
    type: 'Full Time',
    experience: '8+ years',
    postedDate: 'Active',
    summary:
      'Research prospective accounts in targeted markets, pursue leads, and develop a growth strategy focused both on financial gain and customer satisfaction.',
    overview:
      'We are seeking an experienced IT Business Development Specialist with 8+ years of expertise into the same field. You will research prospective enterprise accounts in targeted markets, pursue high-value leads, align strategic business offerings with technical advancements, and lead the complete sales cycle from identification to client registration.',
    sectionHeaders: {
      responsibilities: 'Job Duties',
      requirements: 'Minimum Qualifications',
      niceToHave: 'Strategic Strengths',
      benefits: 'What We Offer',
    },
    responsibilities: [
      'Research prospective accounts in targeted markets, pursue leads and follow through to a successful agreement.',
      'Develop a growth strategy focused both on financial gain and customer satisfaction.',
      'Conduct research to identify new markets and customer needs.',
      'Identify trends in technical advancements and service offerings and define business strategy.',
      'Arrange business meetings with prospective enterprise clients.',
      'Maintain relationships with current clients and identify new prospects within the area you have been assigned.',
      'Possess a strong understanding of our products, our competition in the industry, and market positioning.',
      'Follow the latest industry developments and stay up-to-date on corporate competition.',
      'Actively involved in complete Sales Process: Identification of potential clients, need/requirement analysis, research, and fulfillment of requirement through various sources, offer negotiations, reference checking, and following up till client registration.',
      'Coordinate with the Technical Team during project execution cycle to ensure timely delivery and account management.',
      'Work with business development specialists and help achieve company revenue targets.',
      'Conduct review meetings with senior management on monthly, quarterly, and yearly progress.',
    ],
    requirements: [
      'Minimum Bachelor’s degree with 8+ years of relevant experience in IT business development / enterprise IT sales.',
      'Proven track record in end-to-end B2B sales cycles, lead conversion, and contract negotiations.',
      'Deep understanding of IT solutions, technical advancements, and enterprise positioning.',
      'Exceptional presentation, relationship building, and executive communication skills.',
    ],
    niceToHave: [
      'Existing network of enterprise decision-makers across Canada and North American markets.',
      'Experience in IT services selling, cloud solutions, or digital transformation consulting.',
    ],
    benefits: [
      'Attractive compensation package with performance-based bonuses and sales incentives.',
      'Comprehensive healthcare, medical, and family insurance benefits.',
      'Opportunity to shape go-to-market strategies and collaborate directly with executive leadership.',
      'Flexible hybrid working arrangements.',
    ],
  },
  {
    id: 'sr-business-analyst',
    title: 'Sr. Business Analyst',
    department: 'Business Analyst',
    icon: Briefcase,
    location: 'Canada',
    workplaceType: 'Hybrid',
    type: 'Full Time',
    experience: '5+ years',
    postedDate: 'Active',
    summary:
      'Liaison between BD/Sales and Technical teams, understand client requirements, present technical solutions, and create comprehensive documentation and wireframes.',
    overview:
      'We are looking for an experienced Senior Business Analyst to act as a vital liaison between all departments in the organization. You will work closely with BD/Sales and Technical teams (reporting to BA Leads), understand client requirements, present the technical vision and value propositions, and ensure that proposed solutions strictly align to business goals.',
    sectionHeaders: {
      responsibilities: 'Job Responsibilities',
      requirements: 'Qualifications & Skills',
      niceToHave: 'Leadership & Tools',
      benefits: 'What We Offer',
    },
    responsibilities: [
      'Work closely with the BD/Sales and Technical teams, with reporting to BA Leads.',
      'Act as a liaison between all the departments in the organization.',
      'Understand client’s requirements, grasp, and present the vision, technical solutions, and value propositions.',
      'Ensure that the proposed/design solutions are aligned to client’s business requirements.',
      'Prepare technical docs like understanding document, FRD, BRD, SRS, user stories, architecture diagrams, workflows, flow charts, etc.',
      'Demonstrate strong communication (verbal & written), presentation, organizational, and interpersonal skills.',
      'Utilize tools: Wireframes (Moqups / Balsamiq / Gliffy / Visio) and Project Management Tools (JIRA / Basecamp).',
      'Provide training, mentoring, and guidance to junior business analysts.',
    ],
    requirements: [
      'Proven experience as a Business Analyst / Senior Business Analyst in IT consulting or software engineering.',
      'Expertise in preparing detailed technical documentation (BRD, FRD, SRS, user stories, process flows).',
      'Proficiency with wireframing tools: Moqups, Balsamiq, Gliffy, or Visio.',
      'Proficiency with project management tools: JIRA or Basecamp.',
      'Strong communication (verbal and written), presentation, organizational, and interpersonal skills.',
    ],
    niceToHave: [
      'Experience in leading requirement discovery workshops directly with client executives.',
      'Track record of mentoring junior BAs and standardizing requirement engineering best practices.',
    ],
    benefits: [
      'Competitive compensation package aligned with Canadian industry standards.',
      'Comprehensive health, dental, and wellness insurance coverage.',
      'Dynamic leadership role with direct impact on high-visibility global client projects.',
      'Flexible hybrid working schedule.',
    ],
  },
  {
    id: 'data-scientist',
    title: 'Data Scientist',
    department: 'Analytics',
    icon: Database,
    location: 'Canada, USA, UAE, India',
    workplaceType: 'Hybrid',
    type: 'Full Time',
    experience: '1 year to 5 years',
    postedDate: 'Active',
    summary:
      'Design, build, and deploy machine learning models, fine-tune LLMs, and build end-to-end AI/ML solutions in a fast-growth environment.',
    overview:
      'We are a Google Partner Agency & Google Tag Manager Certified Partner that believes that when the right set of people forms a team they can achieve formidable things. India’s Digital industry is going to grow 10-fold in the next 5 years and we intend to play a strong role in that growth. If you think you have the potential to pace up with a fast growth process driven organization, we are waiting to hear from you.',
    sectionHeaders: {
      responsibilities: 'Responsibilities',
      requirements: 'Qualifications',
      niceToHave: 'Preferred Skills',
      benefits: 'Perks of Permanent Employment',
    },
    responsibilities: [
      'Design, build, and deploy machine learning models for predictive analytics, recommendation systems, and customer segmentation.',
      'Work on supervised, unsupervised, and reinforcement learning techniques to solve business problems.',
      'Develop and fine-tune LLMs (e.g., GPT, BERT, or similar) for applications like natural language processing (NLP), chatbots, and text analytics.',
      'Implement prompt engineering and optimize LLM performance for specific use cases.',
      'Analyze large datasets to identify trends, patterns, and actionable insights.',
      'Perform feature engineering and data preprocessing to improve model performance.',
      'Deploy models into production environments using tools like TensorFlow, PyTorch, or MLflow.',
      'Monitor model performance, retrain models, and implement improvements as needed.',
      'Work closely with data engineers, cloud specialists, and marketing teams to deliver end-to-end AI/ML solutions.',
      'Communicate complex technical concepts to non-technical stakeholders and clients.',
      'Experiment with new tools, frameworks, and methodologies to drive innovation.',
    ],
    requirements: [
      'Bachelor’s or Master’s degree in Computer Science, Data Science, Statistics, or a related field.',
      '3+ years of hands-on experience in data science, AI/ML modeling, and Python programming (overall experience 1 year to 5 years).',
      'Strong expertise in machine learning frameworks like TensorFlow, PyTorch, or Scikit-learn.',
      'Experience with Large Language Models (LLMs) such as GPT, BERT, or similar.',
      'Proficiency in Python and libraries like Pandas, NumPy, and NLTK.',
      'Familiarity with cloud platforms like Google Cloud Platform (GCP), AWS, or Azure.',
      'Strong understanding of NLP techniques and tools (e.g., SpaCy, Hugging Face).',
      'Excellent problem-solving skills and ability to work in a fast-paced environment.',
    ],
    niceToHave: [
      'Experience with prompt engineering and fine-tuning LLMs for specific use cases.',
      'Knowledge of deep learning architectures (e.g., RNNs, CNNs, Transformers).',
      'Familiarity with BigQuery or other cloud-based data warehouses.',
      'Experience in deploying models using Docker or Kubernetes.',
      'Understanding of data governance and AI ethics.',
      'Experience with data visualization tools like Tableau, Power BI, or Looker Studio.',
    ],
    benefits: [
      '5 days working.',
      'Flexible working hours.',
      'Mediclaim for Family.',
      'Maternity Leave benefits.',
      'Paternity Leave benefits.',
      'Birthday Leave benefits.',
      'Bereavement Leave.',
      'Yearly Performance Incentives.',
    ],
  },
];

export const culturePillars = [
  {
    title: 'AI-First by Design',
    description: 'We don’t treat AI as an afterthought. It is central to our tooling, architectures, and everyday problem solving.',
    tag: 'Innovation',
  },
  {
    title: 'Truly Global & Inclusive',
    description: 'Collaborate with multidisciplinary minds across Canada, USA, UAE, and India.',
    tag: 'Global',
  },
  {
    title: 'Autonomous & Outcome-Driven',
    description: 'We value initiative, clarity of execution, and real impact over bureaucratic rituals.',
    tag: 'Impact',
  },
  {
    title: 'Continuous Mastery',
    description: 'Generous learning stipends, certification sponsorships, and dedicated R&D exploration time.',
    tag: 'Growth',
  },
];

export const hiringProcess = [
  { step: '01', title: 'Application Review', description: 'Our talent team reviews your portfolio, experience, and alignment within 3 business days.' },
  { step: '02', title: 'Initial Conversation', description: 'A 30-minute friendly video chat to discuss your background, aspirations, and what drives you.' },
  { step: '03', title: 'Technical / Portfolio Deep Dive', description: 'A collaborative, real-world session focused on practical problem solving rather than trivia.' },
  { step: '04', title: 'Leadership & Team Chat', description: 'Connect with future peers and leadership to align on culture, team vision, and expectations.' },
  { step: '05', title: 'Offer & Onboarding', description: 'A transparent, competitive offer with a seamless setup process from day one.' },
];

export const getAllJobs = () => jobOpenings;

export const getJobById = (id) => jobOpenings.find((j) => j.id === id);

export const getDepartments = () => ['All', ...new Set(jobOpenings.map((j) => j.department))];

export const getWorkplaceTypes = () => ['All', ...new Set(jobOpenings.map((j) => j.workplaceType))];

export const getEmploymentTypes = () => ['All', ...new Set(jobOpenings.map((j) => j.type))];
